import React from 'react';
import { Flex } from '@chakra-ui/react';
import { Modal, Button } from '@morressier/alembic';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { DependentTextForm } from './DependentTextForm';
import { FormFieldDependentText } from '.';
import { IDependentValue } from '@publishing-engine/domain/models';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';

export type IDependentTextValue = {
  [x: string]: {
    [y: string]: IDependentValue<string>;
  };
};

interface Props {
  isOpen: boolean;
  onSubmit: (value: IDependentValue<string>) => void;
  onClose: () => void;
  formField: FormFieldDependentText;
  value?: IDependentValue<string>;
}

export const DependentTextUpdateModal: React.FC<Props> = props => {
  const { isOpen, onSubmit, onClose, formField, value } = props;

  const defaultValues = _.set(
    {} as IDependentTextValue,
    formField.caseDataFields.main,
    value
  );

  const schema = generateValidationSchema({
    [formField.name]: formField
  } as Record<string, FormFieldDefinition>);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const { watch, clearErrors } = methods;
  const dependsOnField = `${formField.caseDataFields.main}.${formField.caseDataFields.dependsOn}`;
  const mainField = `${formField.caseDataFields.main}.value`;
  const hasValue = watch(dependsOnField);

  React.useEffect(() => {
    clearErrors([dependsOnField, mainField]);
  }, [hasValue, dependsOnField, mainField, clearErrors]);

  const handleSubmit = (data: IDependentTextValue) => {
    const formData = _.get(
      data,
      formField.caseDataFields.main
    ) as unknown as IDependentValue<string>;

    const exists = formData?.exists;
    const textValue = formData?.value?.trim();
    const hasNoSection = !!formField.params?.labels?.sectionNo?.explanation;

    const isYesRequired = exists === true && (!textValue || textValue.length === 0);
    const isNoRequired = exists === false && hasNoSection && (!textValue || textValue.length === 0);

    if (isYesRequired || isNoRequired) {
      methods.setError(
        `${formField.caseDataFields.main}.value`,
        {
          type: 'manual',
          //sample text only . it will not refelect in the UI.
          message: 'This field is required'
        }
      );
      return;
    }

    onSubmit(formData);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex flexDirection="column" gap={8} alignItems="flex-start">
        <form
          style={{ width: '100%' }}
          onSubmit={methods.handleSubmit(handleSubmit)}
        >
          <Flex
            padding={8}
            gap={6}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
            flex={1}
          >
            <Flex maxHeight={700} overflow={'auto'}>
              <DependentTextForm config={formField} methods={methods} />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                borderRadius="small"
              >
                Exit Without Saving
              </Button>
              <Button variant="primary" type="submit" borderRadius="small">
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </Modal>
  );
};
